import { requestManager } from ".";

/**
 * =============================================================================
 * Global Interaction Lock
 * =============================================================================
 *
 * Purpose:
 * Temporarily disables all user interactions across the application
 * while there are active async requests.
 *
 * How it works:
 * - Subscribes to RequestManager (single source of truth).
 * - When active request count > 0 → disables pointer events.
 * - When active request count returns to 0 → restores interaction.
 *
 * Implementation detail:
 * Uses `document.body.style.pointerEvents = "none"` to block
 * all mouse / touch interactions globally.
 *
 * Important:
 * - This is independent of Redux.
 * - It reacts instantly (no anti-flicker delay).
 * - It ensures UI cannot be interacted with during critical operations.
 *
 * Use cases:
 * - Prevent duplicate submissions
 * - Avoid race conditions caused by rapid clicks
 * - Enforce atomic flows (payments, auth, destructive actions)
 *
 * ⚠️ Caution:
 * This blocks the entire document.
 * Avoid using this for background requests or polling.
 * =============================================================================
 */

/**
 * Tracks whether interaction is currently locked.
 * Prevents unnecessary DOM writes.
 */
let isLocked: boolean = false;

console.log("Global interaction lock initialized");

/**
 * Subscribe to request count changes.
 * Lock when count > 0.
 * Unlock when count === 0.
 */
requestManager.subscribe((count: number): void => {
  /**
   * Lock interactions when first request starts.
   */
  if (count > 0 && !isLocked) {
    document.body.style.pointerEvents = "none";
    isLocked = true;
  }

  /**
   * Unlock interactions when all requests finish.
   */
  if (count === 0 && isLocked) {
    document.body.style.pointerEvents = "";
    isLocked = false;
  }
});
